import { useState, useEffect, type FormEvent } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '综艺', to: '/category/variety' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : '/search');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-background-50'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1360px] items-center gap-3 px-4 md:gap-6 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-leaf-line text-xl" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
            丝瓜<span className="text-primary-600">视频</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 font-label text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100 hover:text-primary-700'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden w-full max-w-xs items-center gap-2 md:flex">
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="search"
              placeholder="搜索影片、剧集、综艺"
              className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <button
            type="submit"
            className="flex h-10 items-center gap-1 rounded-md bg-primary-500 px-4 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-2-line" />
            <span className="hidden lg:inline">搜索</span>
          </button>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 md:hidden">
          <form onSubmit={submitSearch} className="relative mb-3">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="search"
              placeholder="搜索影片、剧集、综艺"
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-center font-label text-sm font-medium ${
                    isActive ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-800'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}